import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Shield, Users, Lock, Eye, Filter, UserCheck, ShieldCheck,
  ShieldAlert, AlertTriangle, CheckCircle2, XCircle, KeyRound, Ban, ChevronsUpDown, ChevronDown, ChevronRight, X as XIcon, Loader2, Pencil, Trash2, History, Copy, LockKeyhole, LockKeyholeOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { ImpersonationAuditLog } from '@/components/admin/ImpersonationAuditLog';
import { useConfirmDialog } from '@/components/ConfirmDialog';
import UserAvatar from '@/components/UserAvatar';

const PERM_CATEGORIES = {
  dashboards: { label: 'Dashboards', icon: '📊', color: 'text-blue-400' },
  reports: { label: 'Dashboards & Reports', icon: '📈', color: 'text-indigo-400' },
  data_sources: { label: 'Data Sources', icon: '🗄️', color: 'text-emerald-400' },
  semantic_models: { label: 'Models', icon: '🔗', color: 'text-purple-400' },
  measures: { label: 'Measures', icon: '📐', color: 'text-fuchsia-400' },
  models: { label: 'Model Certification', icon: '🧩', color: 'text-fuchsia-400' },
  glossary: { label: 'Glossary', icon: '📖', color: 'text-teal-400' },
  data_prep: { label: 'Data Prep', icon: '🧪', color: 'text-lime-400' },
  alerts: { label: 'Alerts', icon: '🔔', color: 'text-orange-400' },
  schedules: { label: 'Schedules & Bursting', icon: '⏰', color: 'text-amber-400' },
  anomaly: { label: 'Anomaly Scans', icon: '📡', color: 'text-rose-400' },
  discussions: { label: 'Discussions', icon: '💬', color: 'text-sky-400' },
  kpi: { label: 'KPI Monitor', icon: '🎯', color: 'text-green-400' },
  lineage: { label: 'Lineage & Impact', icon: '🕸️', color: 'text-violet-400' },
  marketplace: { label: 'Marketplace', icon: '🛍️', color: 'text-pink-400' },
  embed: { label: 'Embed & Developer APIs', icon: '🧷', color: 'text-cyan-400' },
  ai_copilot: { label: 'Ask AI', icon: '✨', color: 'text-yellow-400' },
  quickstart: { label: 'Quick Start', icon: '🚀', color: 'text-sky-400' },
  apps: { label: 'Apps', icon: '🧱', color: 'text-indigo-400' },
  executive_summaries: { label: 'Executive Summaries', icon: '📝', color: 'text-amber-400' },
  investigations: { label: 'Investigations', icon: '🔍', color: 'text-orange-400' },
  visual_library: { label: 'Visual Library', icon: '🗂️', color: 'text-teal-400' },
  ai_insights: { label: 'AI Insights & Recommendations', icon: '💡', color: 'text-yellow-400' },
  change_requests: { label: 'Change Requests', icon: '🔀', color: 'text-purple-400' },
  users: { label: 'Users', icon: '👥', color: 'text-cyan-400' },
  roles: { label: 'Roles', icon: '🧑‍⚖️', color: 'text-red-400' },
  policies: { label: 'Policies & Governance', icon: '🛡️', color: 'text-red-400' },
  audit: { label: 'Audit Log', icon: '📜', color: 'text-slate-400' },
  data: { label: 'Data Access', icon: '🔓', color: 'text-emerald-400' },
  other: { label: 'Other', icon: '⚙️', color: 'text-muted-foreground' },
};

const PERM_LABELS = {
  'dashboards:read': 'View', 'dashboards:write': 'Create & edit', 'dashboards:delete': 'Delete',
  'reports:read': 'View', 'reports:write': 'Create & edit', 'reports:delete': 'Delete',
  'data_sources:read': 'View', 'data_sources:write': 'Create & edit', 'data_sources:delete': 'Delete',
  'semantic_models:read': 'View', 'semantic_models:write': 'Create & edit', 'semantic_models:delete': 'Delete',
  'measures:write': 'Create & edit', 'measures:delete': 'Delete', 'measures:certify': 'Certify measures',
  'models:read': 'View', 'models:write': 'Create & edit', 'models:delete': 'Delete', 'models:certify': 'Certify models',
  'glossary:read': 'View', 'glossary:write': 'Create & edit', 'glossary:certify': 'Certify terms',
  'data_prep:read': 'View pipelines', 'data_prep:write': 'Build & run',
  'alerts:read': 'View', 'alerts:write': 'Create & edit', 'alerts:delete': 'Delete',
  'schedules:read': 'View', 'schedules:write': 'Create & edit', 'schedules:delete': 'Delete',
  'anomaly:read': 'View', 'anomaly:write': 'Configure & run', 'anomaly:delete': 'Delete',
  'forecast_sentinels:read': 'View', 'forecast_sentinels:write': 'Configure & run', 'forecast_sentinels:delete': 'Delete',
  'quickstart:read': 'View', 'quickstart:write': 'Create',
  'apps:read': 'View', 'apps:write': 'Create & edit', 'apps:delete': 'Delete',
  'visual_library:read': 'View', 'visual_library:write': 'Create & edit', 'visual_library:delete': 'Delete',
  'discussions:read': 'View', 'discussions:write': 'Post & reply',
  'kpi:read': 'View', 'kpi:write': 'Configure',
  'lineage:read': 'View',
  'marketplace:read': 'Browse', 'marketplace:install': 'Install & manage',
  'embed:read': 'View', 'embed:manage': 'Manage keys & embeds',
  'ai_copilot:use': 'Use AI features',
  'ai_copilot:sql_read': 'SQL Library — View', 'ai_copilot:sql_write': 'SQL Library — Create & edit', 'ai_copilot:sql_delete': 'SQL Library — Delete',
  'ai_copilot:ai_response': 'AI Response (raw plan) — Admins only', 'ai_copilot:pin_kpi': 'Pin KPI — Create',
  'ai_copilot:quick_investigation': 'Quick Investigation — View', 'ai_copilot:full_investigation': 'Full Investigation — View',
  'users:read': 'View', 'users:write': 'Manage', 'users:delete': 'Delete',
  'roles:read': 'View', 'roles:write': 'Manage',
  'policies:read': 'View', 'policies:write': 'Manage',
  'audit:read': 'View',
  'data:read_all': 'Read all data', 'data:export': 'Export data',
};

const permActionLabel = (p) => PERM_LABELS[p] || (p.split(':')[1] || p).replace(/_/g, ' ');

function PermissionDisplay({ permissions }) {
  const grouped = {};
  (permissions || []).forEach(p => {
    const parts = p.split(':');
    const cat = parts[0] || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(p);
  });
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
      {Object.entries(grouped).map(([cat, perms]) => {
        const catInfo = PERM_CATEGORIES[cat] || PERM_CATEGORIES.other;
        return (
          <div key={cat} className="rounded-lg border border-border/30 p-2">
            <p className={`text-[10px] font-semibold mb-1 ${catInfo.color}`}>{catInfo.label}</p>
            <div className="flex flex-wrap gap-1">
              {perms.map(p => (
                <Badge key={p} className="text-[8px] bg-muted/30 text-foreground/70 border-border/20">{permActionLabel(p)}</Badge>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RoleBadge({ role }) {
  const styles = {
    admin: 'bg-red-500/15 text-red-400 border-red-500/20',
    editor: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
    analyst: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
    viewer: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/20',
  };
  return <Badge className={`text-[10px] ${styles[role] || styles.viewer}`}>{role}</Badge>;
}

function DepartmentSelect({ value, onChange, departments, testid }) {
  const [adding, setAdding] = useState(false);
  if (adding) {
    return (
      <div className="flex gap-2">
        <Input autoFocus data-testid={testid} value={value} onChange={e => onChange(e.target.value)} placeholder="New department name" className="rounded-lg bg-muted/50 border-border/50" />
        <Button type="button" variant="outline" className="rounded-lg shrink-0" onClick={() => { setAdding(false); onChange(''); }}>Cancel</Button>
      </div>
    );
  }
  return (
    <Select value={value || '__none__'} onValueChange={v => { if (v === '__add__') { setAdding(true); onChange(''); } else onChange(v === '__none__' ? '' : v); }}>
      <SelectTrigger className="rounded-lg bg-muted/50 border-border/50" data-testid={testid}><SelectValue placeholder="Select department" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">Unassigned</SelectItem>
        {departments.map(d => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}
        <SelectItem value="__add__" className="text-primary">+ Add new department…</SelectItem>
      </SelectContent>
    </Select>
  );
}


export default function AccessControlPage() {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const initialTab = ['roles', 'users', 'rls', 'abac', 'denials', 'impersonation'].includes(tabParam) ? tabParam : 'roles';
  const { user: currentUser, setUserData } = useAuth();
  const { confirmFn, ConfirmDialogElement } = useConfirmDialog();
  const canManageUsers = ['admin', 'tenant_admin', 'owner', 'super_admin'].includes(currentUser?.role);
  const [roles, setRoles] = useState([]);
  const [permsOpen, setPermsOpen] = useState(false);
  const [rlsPolicies, setRlsPolicies] = useState([]);
  const [abacPolicies, setAbacPolicies] = useState([]);
  const [users, setUsers] = useState([]);
  const [myPerms, setMyPerms] = useState(null);
  const [denials, setDenials] = useState({ items: [], summary: [] });
  // RLS / ABAC create dialogs
  const [rlsCreateOpen, setRlsCreateOpen] = useState(false);
  const [rlsDraft, setRlsDraft] = useState({ name: '', description: '', model_id: '', entity_id: '', resource_type: '', role: '', conditions: [], enabled: true });
  const [rlsSaving, setRlsSaving] = useState(false);
  const [rlsPreview, setRlsPreview] = useState(null);
  const [rlsPreviewLoading, setRlsPreviewLoading] = useState(false);
  // Cascading model → table → field → values state
  const [rlsModels, setRlsModels] = useState([]);
  const [rlsEntities, setRlsEntities] = useState([]);
  const [rlsModelLoading, setRlsModelLoading] = useState(false);
  const [abacCreateOpen, setAbacCreateOpen] = useState(false);
  const [abacDraft, setAbacDraft] = useState({ name: '', description: '', action: 'write', effect: 'allow', attribute: 'role', operator: 'equals', value: '', enabled: true });
  const [abacSaving, setAbacSaving] = useState(false);
  const [applyingSuggestion, setApplyingSuggestion] = useState('');
  const [loading, setLoading] = useState(true);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetUserId, setResetUserId] = useState('');
  const [resetUserName, setResetUserName] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [editUser, setEditUser] = useState({ user_id: '', name: '', email: '', department: '' });
  const [editSaving, setEditSaving] = useState(false);
  const saveUserEdit = async () => {
    setEditSaving(true);
    try {
      const dep = (editUser.department || '').trim();
      await api.adminUpdateUser(editUser.user_id, { name: editUser.name, email: editUser.email, department: dep });
      setUsers(prev => prev.map(u => u.user_id === editUser.user_id ? { ...u, name: editUser.name, email: editUser.email.trim().toLowerCase(), department: dep || null } : u));
      if (dep && !departments.includes(dep)) setDepartments(prev => [...prev, dep].sort());
      toast.success('User updated');
      setEditUserOpen(false);
    } catch (err) { toast.error(err.message); }
    finally { setEditSaving(false); }
  };
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState('viewer');
  const [newUserDept, setNewUserDept] = useState('');
  const [departments, setDepartments] = useState([]);
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [editRoleOpen, setEditRoleOpen] = useState(false);
  const [editRoleName, setEditRoleName] = useState('');
  const [editRoleDesc, setEditRoleDesc] = useState('');
  const [editRolePersona, setEditRolePersona] = useState('');
  const [editRolePerms, setEditRolePerms] = useState([]);
  const [editRoleIsNew, setEditRoleIsNew] = useState(false);
  const [editRoleLoading, setEditRoleLoading] = useState(false);
  const [allPermissions, setAllPermissions] = useState({});
  const [assignRole, setAssignRole] = useState(null);
  const [expandedRoles, setExpandedRoles] = useState(new Set());
  const toggleRoleExpanded = (name) => setExpandedRoles(prev => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });
  const [assignSel, setAssignSel] = useState(new Set());
  const [assignSaving, setAssignSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [r, rls, abac, u, p] = await Promise.all([
          api.getRoles(),
          api.getRlsPolicies(),
          api.getAbacPolicies(),
          api.getUsers(),
          api.getMyPermissions(),
        ]);
        setRoles(r);
        setRlsPolicies(rls);
        setAbacPolicies(abac);
        setUsers(u);
        setMyPerms(p);
        try { const ap = await api.getAllPermissions(); setAllPermissions(ap.categories || {}); } catch { /* permissions optional */ }
        try { const d = await api.getRbacDenials(100); setDenials({ items: d.items || [], summary: d.summary || [] }); } catch { /* audit:read required — optional */ }
      } catch (err) {
        console.error('Access control load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const toggleRlsPolicy = async (policyId) => {
    try {
      const result = await api.toggleRlsPolicy(policyId);
      setRlsPolicies(prev => prev.map(p => p.policy_id === policyId ? {...p, enabled: result.enabled} : p));
      toast.success(`Policy ${result.enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const toggleAbacPolicy = async (policyId) => {
    try {
      const result = await api.toggleAbacPolicy(policyId);
      setAbacPolicies(prev => prev.map(p => p.policy_id === policyId ? {...p, enabled: result.enabled} : p));
      toast.success(`Policy ${result.enabled ? 'enabled' : 'disabled'}`);
    } catch (err) { toast.error(err.message); }
  };

  const parseValue = (raw) => {
    const v = (raw ?? '').trim();
    if (v === '') return '';
    if (v.startsWith('[') || v.startsWith('{')) { try { return JSON.parse(v); } catch { return v; } }
    if (v === 'true') return true;
    if (v === 'false') return false;
    if (!isNaN(Number(v)) && v !== '') return Number(v);
    // Comma list → array
    if (v.includes(',')) return v.split(',').map(s => s.trim()).filter(Boolean);
    return v;
  };

  const NUMERIC_TYPES = ['integer', 'int', 'float', 'number', 'decimal', 'numeric', 'double'];
  const DATE_TYPES = ['date', 'datetime', 'timestamp', 'time', 'datetime2', 'datetimeoffset'];
  const NUMERIC_OPERATORS = [
    { v: 'eq', label: 'equals (=)' },
    { v: 'gt', label: 'greater than (>)' },
    { v: 'gte', label: 'greater or equal (≥)' },
    { v: 'lt', label: 'less than (<)' },
    { v: 'lte', label: 'less or equal (≤)' },
    { v: 'between', label: 'between' },
  ];
  const DATE_OPERATORS = [
    { v: 'on_or_after', label: 'on or after' },
    { v: 'on_or_before', label: 'on or before' },
    { v: 'between', label: 'between' },
    { v: 'rolling', label: 'in the last N days (rolling)' },
  ];

  const newCondition = () => ({ key: Math.random().toString(36).slice(2), field: '', field_type: '', operator: '', value: '', value2: '', values: [], options: [], optionsLoading: false, truncated: false, pickerOpen: false });

  const buildConditionFragment = (c) => {
    const field = (c.field || '').trim();
    if (!field) return null;
    if (c.field_type === 'date') {
      if (c.operator === 'rolling') {
        const n = Number(c.value);
        if (c.value === '' || isNaN(n) || n <= 0) return null;
        return { [field]: { $rolling_days: Math.round(n) } };
      }
      if (c.operator === 'between') {
        if (!c.value || !c.value2) return null;
        const lo = c.value <= c.value2 ? c.value : c.value2;
        const hi = c.value <= c.value2 ? c.value2 : c.value;
        return { [field]: { $gte: lo, $lte: hi } };
      }
      if (!c.value) return null;
      const op = c.operator === 'on_or_before' ? '$lte' : '$gte';
      return { [field]: { [op]: c.value } };
    }
    if (c.field_type === 'numeric') {
      if (c.operator === 'between') {
        const a = Number(c.value), b = Number(c.value2);
        if (c.value === '' || c.value2 === '' || isNaN(a) || isNaN(b)) return null;
        return { [field]: { $gte: Math.min(a, b), $lte: Math.max(a, b) } };
      }
      const n = Number(c.value);
      if (c.value === '' || isNaN(n)) return null;
      const opMap = { gt: '$gt', gte: '$gte', lt: '$lt', lte: '$lte' };
      if (!c.operator || c.operator === 'eq') return { [field]: n };
      return { [field]: { [opMap[c.operator]]: n } };
    }
    const vals = c.values || [];
    if (vals.length === 0) return null;
    return { [field]: vals.length === 1 ? vals[0] : { $in: vals } };
  };

  const buildRlsFilterCondition = () => {
    const conds = rlsDraft.conditions || [];
    if (conds.length === 0) return null;
    const merged = {};
    for (const c of conds) {
      const frag = buildConditionFragment(c);
      if (!frag) return null; // an incomplete condition blocks save
      const [k, v] = Object.entries(frag)[0];
      merged[k] = v;
    }
    return Object.keys(merged).length ? merged : null;
  };

  // ── Cascading model → table → field → values ──
  const loadRlsModels = async () => {
    setRlsModelLoading(true);
    try {
      const models = await api.getSemanticModels();
      setRlsModels(models || []);
    } catch (err) { toast.error('Could not load models'); }
    finally { setRlsModelLoading(false); }
  };

  const openRlsCreate = () => {
    setRlsDraft({ name: '', description: '', model_id: '', entity_id: '', resource_type: '', role: '', conditions: [newCondition()], enabled: true });
    setRlsEntities([]); setRlsPreview(null);
    setRlsCreateOpen(true);
    if (rlsModels.length === 0) loadRlsModels();
  };

  const onSelectRlsModel = async (modelId) => {
    setRlsDraft(d => ({ ...d, model_id: modelId, entity_id: '', resource_type: '', conditions: [newCondition()] }));
    setRlsEntities([]); setRlsPreview(null);
    setRlsModelLoading(true);
    try {
      const full = await api.getSemanticModelFull(modelId);
      setRlsEntities(full?.entities || []);
    } catch (err) { toast.error('Could not load tables for this model'); }
    finally { setRlsModelLoading(false); }
  };

  const rlsCurrentEntity = rlsEntities.find(e => e.entity_id === rlsDraft.entity_id) || null;
  const rlsEntityAttrs = (rlsCurrentEntity?.attributes || []);

  const onSelectRlsEntity = (entityId) => {
    const ent = rlsEntities.find(e => e.entity_id === entityId);
    setRlsDraft(d => ({ ...d, entity_id: entityId, resource_type: ent?.table_name || '', conditions: [newCondition()] }));
    setRlsPreview(null);
  };

  const addCondition = () => setRlsDraft(d => ({ ...d, conditions: [...d.conditions, newCondition()] }));
  const removeCondition = (key) => setRlsDraft(d => ({ ...d, conditions: d.conditions.length > 1 ? d.conditions.filter(c => c.key !== key) : d.conditions }));
  const updateCondition = (key, patch) => setRlsDraft(d => ({ ...d, conditions: d.conditions.map(c => c.key === key ? { ...c, ...patch } : c) }));

  const onSelectConditionField = async (key, fieldName) => {
    const attr = rlsEntityAttrs.find(a => a.name === fieldName);
    const dt = (attr?.data_type || '').toLowerCase();
    const isDate = DATE_TYPES.includes(dt);
    const isNumeric = NUMERIC_TYPES.includes(dt);
    if (isDate) {
      updateCondition(key, { field: fieldName, field_type: 'date', operator: 'on_or_after', value: '', value2: '', values: [], options: [], truncated: false, optionsLoading: false });
      setRlsPreview(null);
      return;
    }
    updateCondition(key, { field: fieldName, field_type: isNumeric ? 'numeric' : 'text', operator: isNumeric ? 'eq' : 'in', value: '', value2: '', values: [], options: [], truncated: false, optionsLoading: !isNumeric });
    setRlsPreview(null);
    if (!isNumeric && rlsDraft.resource_type) {
      try {
        const res = await api.getRlsFieldValues(rlsDraft.resource_type, fieldName);
        if (res && res.field_kind === 'numeric' && (res.values || []).length === 0) {
          updateCondition(key, { field_type: 'numeric', operator: 'eq', optionsLoading: false });
        } else {
          updateCondition(key, { options: res.values || [], truncated: !!res.truncated, optionsLoading: false });
        }
      } catch (err) { updateCondition(key, { options: [], optionsLoading: false }); }
    }
  };

  const toggleConditionValue = (key, val) => setRlsDraft(d => ({
    ...d,
    conditions: d.conditions.map(c => {
      if (c.key !== key) return c;
      const has = c.values.some(v => String(v) === String(val));
      return { ...c, values: has ? c.values.filter(v => String(v) !== String(val)) : [...c.values, val] };
    }),
  }));

  const previewRls = async () => {
    const cond = buildRlsFilterCondition();
    if (!rlsDraft.resource_type.trim() || !cond) {
      toast.error('Complete every condition (field + value) to preview');
      return;
    }
    setRlsPreviewLoading(true);
    try {
      const res = await api.previewRlsPolicy({
        resource_type: rlsDraft.resource_type.trim(),
        filter_condition: cond,
        role: rlsDraft.role || null,
      });
      setRlsPreview(res);
    } catch (err) { toast.error(err.message); }
    finally { setRlsPreviewLoading(false); }
  };

  const handleCreateRls = async () => {
    const cond = buildRlsFilterCondition();
    if (!rlsDraft.name.trim() || !rlsDraft.resource_type.trim() || !cond) {
      toast.error('Name, table and at least one complete condition are required');
      return;
    }
    setRlsSaving(true);
    try {
      const created = await api.createRbacRlsPolicy({
        name: rlsDraft.name.trim(),
        description: rlsDraft.description.trim(),
        resource_type: rlsDraft.resource_type.trim(),
        filter_condition: cond,
        role: rlsDraft.role || null,
        scope: rlsDraft.role ? 'role' : 'tenant',
        enabled: rlsDraft.enabled,
      });
      setRlsPolicies(prev => [...prev, created]);
      setRlsCreateOpen(false);
      setRlsDraft({ name: '', description: '', model_id: '', entity_id: '', resource_type: '', role: '', conditions: [newCondition()], enabled: true });
      toast.success('Row-level policy created');
    } catch (err) { toast.error(err.message); }
    finally { setRlsSaving(false); }
  };

  const handleCreateAbac = async () => {
    if (!abacDraft.name.trim() || !abacDraft.action.trim() || !abacDraft.attribute.trim()) {
      toast.error('Name, action and attribute are required');
      return;
    }
    setAbacSaving(true);
    try {
      const created = await api.createRbacAbacPolicy({
        name: abacDraft.name.trim(),
        description: abacDraft.description.trim(),
        action: abacDraft.action.trim(),
        effect: abacDraft.effect,
        enabled: abacDraft.enabled,
        conditions: [{ attribute_source: 'user', attribute: abacDraft.attribute.trim(), operator: abacDraft.operator, value: parseValue(abacDraft.value) }],
      });
      setAbacPolicies(prev => [...prev, created]);
      setAbacCreateOpen(false);
      setAbacDraft({ name: '', description: '', action: 'write', effect: 'allow', attribute: 'role', operator: 'equals', value: '', enabled: true });
      toast.success('ABAC policy created');
    } catch (err) { toast.error(err.message); }
    finally { setAbacSaving(false); }
  };

  // Role suggestions from the denial log: for each (role, permission) pair that
  // keeps 403-ing, suggest granting it. Only CUSTOM roles can be edited (built-in
  // roles are code-defined), so those get a one-click Apply.
  const roleSuggestions = React.useMemo(() => {
    return (denials.summary || []).map(s => {
      const roleDef = roles.find(r => r.role_name === s.role);
      const isCustom = roleDef && !roleDef.is_system;
      const already = roleDef?.permissions?.includes(s.permission);
      return { ...s, isCustom: !!isCustom, already: !!already };
    }).filter(s => !s.already && s.permission);
  }, [denials.summary, roles]);

  const applySuggestion = async (sug) => {
    const roleDef = roles.find(r => r.role_name === sug.role);
    if (!roleDef || roleDef.is_system) return;
    setApplyingSuggestion(`${sug.role}:${sug.permission}`);
    try {
      const nextPerms = Array.from(new Set([...(roleDef.permissions || []), sug.permission]));
      const updated = await api.updateRole(sug.role, { description: roleDef.description || '', permissions: nextPerms });
      setRoles(prev => prev.map(r => r.role_name === sug.role ? updated : r));
      toast.success(`Granted ${sug.permission} to ${sug.role}`);
    } catch (err) { toast.error(err.message); }
    finally { setApplyingSuggestion(''); }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await api.updateUserRole(userId, newRole);
      setUsers(prev => prev.map(u => u.user_id === userId ? {...u, role: newRole} : u));
      toast.success('Role updated');
      try { const r = await api.getRoles(); setRoles(r); } catch { /* count refresh optional */ }
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Assign a role to users from the role card ("who has this role").
  const openAssign = (role) => {
    setAssignRole(role);
    setAssignSel(new Set(users.filter(u => u.role === role.role_name).map(u => u.user_id)));
  };
  const toggleAssign = (uid) => setAssignSel(prev => {
    const n = new Set(prev); if (n.has(uid)) n.delete(uid); else n.add(uid); return n;
  });
  const saveAssign = async () => {
    if (!assignRole) return;
    setAssignSaving(true);
    try {
      const roleName = assignRole.role_name;
      const changes = [];
      for (const u of users) {
        const shouldHave = assignSel.has(u.user_id);
        const has = u.role === roleName;
        if (shouldHave && !has) changes.push({ id: u.user_id, role: roleName });
        else if (!shouldHave && has) changes.push({ id: u.user_id, role: 'viewer' });
      }
      for (const c of changes) await api.updateUserRole(c.id, c.role);
      if (changes.length) {
        setUsers(prev => prev.map(u => { const c = changes.find(x => x.id === u.user_id); return c ? { ...u, role: c.role } : u; }));
        try { const r = await api.getRoles(); setRoles(r); } catch { /* optional */ }
      }
      toast.success(changes.length ? `Updated ${changes.length} assignment(s)` : 'No changes');
      setAssignRole(null);
    } catch (e) { toast.error(e.message || 'Failed to update assignments'); }
    finally { setAssignSaving(false); }
  };

  const passwordRules = (pw) => ([
    { label: 'At least 10 characters', ok: pw.length >= 10 },
    { label: 'One lowercase letter', ok: /[a-z]/.test(pw) },
    { label: 'One uppercase letter', ok: /[A-Z]/.test(pw) },
    { label: 'One digit', ok: /[0-9]/.test(pw) },
    { label: 'One symbol (e.g. !@#$%)', ok: /[^A-Za-z0-9]/.test(pw) },
  ]);
  const passwordMeetsRules = (pw) => passwordRules(pw).every(r => r.ok);

  const handleResetPassword = async () => {
    if (!passwordMeetsRules(resetPassword)) {
      toast.error('Password does not meet all requirements');
      return;
    }
    setResetLoading(true);
    try {
      await api.adminResetPassword(resetUserId, resetPassword);
      toast.success('Password reset successfully');
      setResetOpen(false);
      setResetPassword('');
    } catch (err) { toast.error(err.message); }
    finally { setResetLoading(false); }
  };

  const isLocked = (u) => !!u.locked_until && new Date(u.locked_until) > new Date();

  const handleUnlockUser = async (u) => {
    try {
      const res = await api.adminUnlockAccount(u.user_id);
      setUsers(prev => prev.map(x => x.user_id === u.user_id ? { ...x, locked_until: null, lock_reason: null } : x));
      toast.success(`Unlocked ${res.email || u.email}`);
    } catch (err) { toast.error(err.message); }
  };

  useEffect(() => { api.getDepartments().then(r => setDepartments(r.departments || [])).catch(() => {}); }, []);

  const handleCreateUser = async () => {
    if (!newUserEmail) { toast.error('Email is required'); return; }
    if (!passwordMeetsRules(newUserPassword)) { toast.error('Password does not meet all requirements'); return; }
    setCreateUserLoading(true);
    try {
      const created = await api.adminCreateUser({ email: newUserEmail, name: newUserName, password: newUserPassword, role: newUserRole, department: newUserDept.trim() || undefined });
      setUsers(prev => [...prev, created]);
      if (newUserDept.trim() && !departments.includes(newUserDept.trim())) setDepartments(prev => [...prev, newUserDept.trim()].sort());
      setCreateUserOpen(false);
      setNewUserName(''); setNewUserEmail(''); setNewUserPassword(''); setNewUserRole('viewer'); setNewUserDept('');
      toast.success('User created: ' + created.email);
    } catch (err) { toast.error(err.message); }
    finally { setCreateUserLoading(false); }
  };

  const openEditRole = async (role) => {
    setEditRoleIsNew(false);
    setEditRoleName(role.role_name);
    setEditRoleDesc(role.description || '');
    setEditRolePersona(role.ai_persona || '');
    setEditRolePerms([...(role.permissions || [])]);
    // Always fetch permissions fresh when dialog opens
    try { const ap = await api.getAllPermissions(); setAllPermissions(ap.categories || {}); } catch (err) { console.error('Failed to load permissions:', err); }
    setEditRoleOpen(true);
  };

  const openCreateRole = async () => {
    setEditRoleIsNew(true);
    setEditRoleName('');
    setEditRoleDesc('');
    setEditRolePersona('');
    setEditRolePerms([]);
    try { const ap = await api.getAllPermissions(); setAllPermissions(ap.categories || {}); } catch (err) { console.error('Failed to load permissions:', err); }
    setEditRoleOpen(true);
  };

  // Clone any role (built-in or custom) into a new custom role pre-filled with
  // its permissions, so admins can start from a default and tweak it.
  const openCloneRole = async (role) => {
    setEditRoleIsNew(true);
    setEditRoleName(`${role.role_name}_copy`);
    setEditRoleDesc(role.description ? `Copy of ${role.role_name} — ${role.description}` : `Copy of ${role.role_name}`);
    setEditRolePersona(role.ai_persona || '');
    let perms = [...(role.permissions || [])];
    try {
      const full = await api.getRole(role.role_name);
      if (full && Array.isArray(full.permissions)) perms = [...full.permissions];
    } catch (err) { console.error('Failed to load role permissions:', err); }
    setEditRolePerms(perms);
    try { const ap = await api.getAllPermissions(); setAllPermissions(ap.categories || {}); } catch (err) { console.error('Failed to load permissions:', err); }
    setEditRoleOpen(true);
  };

  const handleSaveRole = async () => {
    if (!editRoleName.trim()) { toast.error('Role name required'); return; }
    setEditRoleLoading(true);
    try {
      if (editRoleIsNew) {
        const created = await api.createRole({ role_name: editRoleName, description: editRoleDesc, ai_persona: editRolePersona, permissions: editRolePerms });
        setRoles(prev => [...prev, created]);
        toast.success('Role created');
      } else {
        const updated = await api.updateRole(editRoleName, { description: editRoleDesc, ai_persona: editRolePersona, permissions: editRolePerms });
        setRoles(prev => prev.map(r => r.role_name === editRoleName ? updated : r));
        toast.success('Role updated');
      }
      setEditRoleOpen(false);
    } catch (err) { toast.error(err.message); }
    finally { setEditRoleLoading(false); }
  };

  const handleDeleteRole = async (roleName) => {
    try {
      await api.deleteRole(roleName);
      setRoles(prev => prev.filter(r => r.role_name !== roleName));
      toast.success('Role deleted');
    } catch (err) { toast.error(err.message); }
  };

  const toggleUserStatus = async (u) => {
    const disabling = (u.status || 'active') !== 'disabled';
    if (disabling) {
      const ok = await confirmFn({
        title: 'Disable this user?',
        message: `${u.name || u.email} will be signed out immediately and blocked from logging in until you re-enable them.`,
        confirmLabel: 'Disable user',
        destructive: true,
      });
      if (!ok) return;
    }
    try {
      const res = await api.updateUserStatus(u.user_id, { status: disabling ? 'disabled' : 'active' });
      setUsers(prev => prev.map(x => x.user_id === u.user_id ? { ...x, status: res.status } : x));
      toast.success(disabling ? `${u.name || u.email} disabled` : `${u.name || u.email} enabled`);
    } catch (err) { toast.error(err.message); }
  };

  const handleDeleteUser = async (u) => {
    const ok = await confirmFn({
      title: 'Delete this user?',
      message: `This permanently removes ${u.name || u.email} and their sessions, favorites and profile. This cannot be undone.`,
      confirmLabel: 'Delete user',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.deleteUser(u.user_id);
      setUsers(prev => prev.filter(x => x.user_id !== u.user_id));
      toast.success(`${u.name || u.email} deleted`);
    } catch (err) { toast.error(err.message); }
  };

  const togglePerm = (perm) => {
    const removing = editRolePerms.includes(perm);
    if (removing && perm === 'ai_copilot:ai_response') {
      toast('Heads up: the AI Response (raw plan) button will disappear for users with this role.', {
        icon: '👁️', duration: 5000,
      });
    }
    setEditRolePerms(prev => prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]);
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        {[...Array(3)].map((_, i) => <div key={i} className="glass-card rounded-2xl p-6 h-20 animate-pulse" />)}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="access-control-page">
      <div className="animate-slide-up">
        <h1 className="text-3xl font-heading font-bold tracking-tight text-foreground">Access Control</h1>
        <p className="text-sm text-muted-foreground mt-1">RBAC, ABAC, Row-Level & Column-Level Security</p>
      </div>

      {/* My Permissions Summary */}
      {myPerms && (
        <div className="glass-card rounded-2xl p-5 animate-slide-up" style={{ animationDelay: '50ms' }} data-testid="my-permissions">
          <button
            type="button"
            onClick={() => setPermsOpen(o => !o)}
            className="w-full flex items-center gap-3 text-left"
            data-testid="my-permissions-toggle"
            aria-expanded={permsOpen}
          >
            <div className="p-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
              <ShieldCheck className="w-5 h-5 text-emerald-400" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-heading font-semibold text-foreground">Your Permissions</p>
              <p className="text-xs text-muted-foreground">Role: <RoleBadge role={myPerms.role} /></p>
            </div>
            <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${permsOpen ? 'rotate-180' : ''}`} />
          </button>
          {permsOpen && (
            <div className="mt-3" data-testid="my-permissions-body">
              <PermissionDisplay permissions={myPerms.permissions} />
            </div>
          )}
        </div>
      )}

      <Tabs defaultValue={initialTab} className="space-y-4">
        <TabsList className="bg-muted/50 border border-border/50 rounded-xl p-1">
          <TabsTrigger value="roles" className="rounded-lg text-xs data-[state=active]:bg-primary/20 data-[state=active]:text-primary" data-testid="tab-roles">
            <Shield className="w-3.5 h-3.5 mr-1.5" /> Roles
          </TabsTrigger>
          <TabsTrigger value="users" className="rounded-lg text-xs data-[state=active]:bg-primary/20 data-[state=active]:text-primary" data-testid="tab-users">
            <Users className="w-3.5 h-3.5 mr-1.5" /> Users
          </TabsTrigger>
          <TabsTrigger value="rls" className="rounded-lg text-xs data-[state=active]:bg-primary/20 data-[state=active]:text-primary" data-testid="tab-rls">
            <Filter className="w-3.5 h-3.5 mr-1.5" /> Row-Level Security
          </TabsTrigger>
          <TabsTrigger value="abac" className="rounded-lg text-xs data-[state=active]:bg-primary/20 data-[state=active]:text-primary" data-testid="tab-abac">
            <Lock className="w-3.5 h-3.5 mr-1.5" /> ABAC Policies
          </TabsTrigger>
          <TabsTrigger value="denials" className="rounded-lg text-xs data-[state=active]:bg-primary/20 data-[state=active]:text-primary" data-testid="tab-denials">
            <Ban className="w-3.5 h-3.5 mr-1.5" /> Denials
          </TabsTrigger>
          {canManageUsers && (
            <TabsTrigger value="impersonation" className="rounded-lg text-xs data-[state=active]:bg-primary/20 data-[state=active]:text-primary" data-testid="tab-impersonation">
              <History className="w-3.5 h-3.5 mr-1.5" /> Impersonation Log
            </TabsTrigger>
          )}
        </TabsList>

        {/* Roles Tab */}
        <TabsContent value="roles" className="space-y-3">
          <div className="flex justify-end">
            <Button onClick={openCreateRole} className="rounded-xl bg-primary text-primary-foreground active:scale-95 text-xs" data-testid="create-role-btn">
              <Shield className="w-3.5 h-3.5 mr-1.5" /> Create Role
            </Button>
          </div>
          <div className="stagger-children" data-testid="roles-list">
            {roles.map(role => { const isExpanded = expandedRoles.has(role.role_name); return (
              <div key={role.role_name} className="glass-card rounded-2xl p-5 hover:border-primary/20 transition-all group" data-testid={`role-${role.role_name}`}>
                <div className="flex items-center justify-between">
                  <button onClick={() => toggleRoleExpanded(role.role_name)} className="flex items-center gap-3 text-left flex-1 min-w-0" data-testid={`role-toggle-${role.role_name}`} aria-expanded={isExpanded}>
                    <div className="text-muted-foreground shrink-0">
                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </div>
                    <div className="p-2 rounded-xl bg-primary/10 border border-primary/20 shrink-0">
                      <Shield className="w-5 h-5 text-primary" strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-heading font-semibold text-foreground capitalize">{role.role_name}</h3>
                        {role.is_system && <Badge variant="secondary" className="text-[9px]">System</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{role.description}</p>
                    </div>
                  </button>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className="text-xs border-border/50 text-muted-foreground">
                      {role.permissions.length} permissions
                    </Badge>
                    <Badge variant="outline" className="text-xs border-primary/30 text-primary" data-testid={`role-user-count-${role.role_name}`}>
                      {role.user_count || 0} {role.user_count === 1 ? 'user' : 'users'}
                    </Badge>
                    <Button variant="outline" size="sm" onClick={() => openAssign(role)} className="rounded-lg text-xs h-7 gap-1" data-testid={`assign-users-${role.role_name}`}>
                      <UserCheck className="w-3 h-3" /> Assign users
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openCloneRole(role)} className="rounded-lg text-xs h-7 gap-1" data-testid={`clone-role-${role.role_name}`}>
                      <Copy className="w-3 h-3" /> Clone
                    </Button>
                    {!role.is_system && (
                      <Button variant="outline" size="sm" onClick={() => openEditRole(role)} className="rounded-lg text-xs h-7 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`edit-role-${role.role_name}`}>
                        Edit
                      </Button>
                    )}
                    {!role.is_system && (
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteRole(role.role_name)} className="rounded-lg text-xs h-7 text-destructive opacity-0 group-hover:opacity-100 transition-opacity">
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
                {isExpanded && (<>
                <div className="mt-3">
                {(() => { const assigned = users.filter(u => u.role === role.role_name); return assigned.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`role-members-${role.role_name}`}>
                    {assigned.slice(0, 12).map(u => (
                      <span key={u.user_id} className="px-2 py-0.5 rounded-md text-[11px] bg-muted/40 border border-border/40 text-foreground/80">{u.name || u.email}</span>
                    ))}
                    {assigned.length > 12 && <span className="px-2 py-0.5 text-[11px] text-muted-foreground">+{assigned.length - 12} more</span>}
                  </div>
                ) : (
                  <p className="text-[11px] text-muted-foreground mb-3" data-testid={`role-members-${role.role_name}`}>No users assigned yet.</p>
                ); })()}
                <PermissionDisplay permissions={role.permissions} />
                </div>
                </>)}
              </div>
            ); })}
          </div>
          {/* Assign users to a role */}
          <Dialog open={!!assignRole} onOpenChange={(o) => { if (!o) setAssignRole(null); }}>
            <DialogContent className="max-w-md" data-testid="assign-users-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-sm capitalize">
                  <UserCheck className="w-4 h-4 text-primary" /> Assign users to "{assignRole?.role_name}"
                </DialogTitle>
              </DialogHeader>
              <p className="text-[11px] text-muted-foreground -mt-1">Check the people who should have this role. Unchecking someone who currently has it moves them to Viewer.</p>
              <div className="max-h-72 overflow-y-auto space-y-1 mt-1">
                {users.length === 0 ? (
                  <div className="text-center py-6 text-xs text-muted-foreground">No users in this tenant.</div>
                ) : users.map(u => (
                  <label key={u.user_id} className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-muted/40 cursor-pointer" data-testid={`assign-user-${u.user_id}`}>
                    <Checkbox checked={assignSel.has(u.user_id)} onCheckedChange={() => toggleAssign(u.user_id)} />
                    <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">{u.name?.[0]?.toUpperCase() || 'U'}</div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-foreground truncate">{u.name || u.email}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{u.email} · currently {u.role}</p>
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex justify-end gap-2 mt-2">
                <Button variant="outline" size="sm" onClick={() => setAssignRole(null)} className="text-xs" data-testid="assign-cancel">Cancel</Button>
                <Button size="sm" onClick={saveAssign} disabled={assignSaving} className="text-xs" data-testid="assign-save">
                  {assignSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null} Save assignments
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>
        <TabsContent value="users" className="space-y-3">
          <div className="flex justify-end">
            <Button onClick={() => setCreateUserOpen(true)} className="rounded-xl bg-primary text-primary-foreground active:scale-95 text-xs" data-testid="create-user-btn">
              <Users className="w-3.5 h-3.5 mr-1.5" /> Create User
            </Button>
          </div>
          <div className="stagger-children" data-testid="users-list">
            {users.map(u => (
              <div key={u.user_id} className={`glass-card rounded-2xl p-5 flex items-center justify-between ${(u.status === 'disabled') ? 'opacity-60' : ''}`} data-testid={`user-${u.user_id}`}>
                <div className="flex items-center gap-3">
                  <UserAvatar name={u.name || u.email} src={u.avatarUrl} size={36} />
                  <div>
                    <p className="text-sm font-medium text-foreground flex items-center gap-2">
                      {u.name}
                      {u.status === 'disabled' && (
                        <Badge variant="outline" className="text-[9px] border-destructive/40 text-destructive" data-testid={`user-disabled-badge-${u.user_id}`}>Disabled</Badge>
                      )}
                      {isLocked(u) && (
                        <Badge variant="outline" className="text-[9px] border-amber-500/40 text-amber-400 gap-1" data-testid={`user-locked-badge-${u.user_id}`}><LockKeyhole className="w-2.5 h-2.5" /> Locked</Badge>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {canManageUsers && isLocked(u) && (
                    <Button variant="outline" size="sm" onClick={() => handleUnlockUser(u)} className="rounded-lg text-xs h-8 gap-1 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 border-amber-500/40" data-testid={`unlock-user-${u.user_id}`}>
                      <LockKeyholeOpen className="w-3 h-3" /> Unlock
                    </Button>
                  )}
                  <Select value={u.role} onValueChange={val => updateUserRole(u.user_id, val)}>
                    <SelectTrigger className="w-32 rounded-lg bg-muted/50 border-border/50 text-xs h-8" data-testid={`role-select-${u.user_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map(r => (
                        <SelectItem key={r.role_name} value={r.role_name} className="text-xs capitalize">{r.role_name.replace(/_/g, ' ')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={() => { setResetUserId(u.user_id); setResetUserName(u.name || u.email); setResetPassword(''); setResetOpen(true); }} className="rounded-lg text-xs h-8 gap-1" data-testid={`reset-pw-${u.user_id}`}>
                    <KeyRound className="w-3 h-3" /> Reset
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setEditUser({ user_id: u.user_id, name: u.name || '', email: u.email || '', department: u.department || '' }); setEditUserOpen(true); }} className="rounded-lg text-xs h-8 gap-1" data-testid={`edit-user-${u.user_id}`}>
                    <Pencil className="w-3 h-3" /> Edit
                  </Button>
                  {['admin', 'tenant_admin', 'owner', 'super_admin'].includes(currentUser?.role) && currentUser?.user_id !== u.user_id && u.role !== 'super_admin' && (
                    <Button variant="outline" size="sm" onClick={async () => {
                      try {
                        const result = await api.impersonateUser(u.user_id);
                        // Store admin user info for end-impersonation UI (no tokens in localStorage)
                        localStorage.setItem('impersonating', JSON.stringify({ admin_user: currentUser, return_to: window.location.pathname + window.location.search }));
                        toast.success(`Now viewing as ${u.name || u.email}`);
                        window.location.href = '/dashboard';
                      } catch (err) { toast.error(err.message); }
                    }} className="rounded-lg text-xs h-8 gap-1 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10" data-testid={`impersonate-${u.user_id}`}>
                      <Eye className="w-3 h-3" /> View As
                    </Button>
                  )}
                  {canManageUsers && currentUser?.user_id !== u.user_id && u.role !== 'super_admin' && (
                    <Button variant="outline" size="sm" onClick={() => toggleUserStatus(u)} className={`rounded-lg text-xs h-8 gap-1 ${u.status === 'disabled' ? 'text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10' : 'text-orange-400 hover:text-orange-300 hover:bg-orange-500/10'}`} data-testid={`toggle-status-${u.user_id}`}>
                      {u.status === 'disabled' ? <><CheckCircle2 className="w-3 h-3" /> Enable</> : <><Ban className="w-3 h-3" /> Disable</>}
                    </Button>
                  )}
                  {canManageUsers && currentUser?.user_id !== u.user_id && u.role !== 'super_admin' && (
                    <Button variant="outline" size="sm" onClick={() => handleDeleteUser(u)} className="rounded-lg text-xs h-8 gap-1 text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`delete-user-${u.user_id}`}>
                      <Trash2 className="w-3 h-3" /> Delete
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* RLS Tab */}
        <TabsContent value="rls" className="space-y-3">
          <div className="glass-card rounded-2xl p-4 bg-amber-500/5 border-amber-500/20 mb-3 flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-300/80">Row-Level Security policies filter data at query time. Enabling a policy restricts what data users with matching roles can see.</p>
          </div>
          <div className="flex justify-end">
            <Button onClick={openRlsCreate} className="rounded-xl bg-primary text-primary-foreground active:scale-95 text-xs" data-testid="create-rls-btn">
              <Filter className="w-3.5 h-3.5 mr-1.5" /> Add Policy
            </Button>
          </div>
          <div className="stagger-children" data-testid="rls-policies-list">
            {rlsPolicies.map(policy => (
              <div key={policy.policy_id} className="glass-card rounded-2xl p-5" data-testid={`rls-${policy.policy_id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-xl ${policy.enabled ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-zinc-500/10 border-zinc-500/20'} border`}>
                      {policy.enabled ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <XCircle className="w-5 h-5 text-zinc-500" />}
                    </div>
                    <div>
                      <h3 className="text-sm font-heading font-semibold text-foreground">{policy.name}</h3>
                      <p className="text-xs text-muted-foreground">{policy.description}</p>
                      <div className="flex gap-2 mt-1.5">
                        <Badge variant="outline" className="text-[9px] border-border/50">Resource: {policy.resource_type}</Badge>
                        <Badge variant="outline" className="text-[9px] border-border/50">Scope: {policy.scope}</Badge>
                        {policy.role && <RoleBadge role={policy.role} />}
                      </div>
                    </div>
                  </div>
                  <Switch
                    checked={policy.enabled}
                    onCheckedChange={() => toggleRlsPolicy(policy.policy_id)}
                    data-testid={`rls-toggle-${policy.policy_id}`}
                  />
                </div>
                <div className="mt-3 p-2 rounded-lg bg-muted/30 font-mono text-[10px] text-muted-foreground overflow-x-auto">
                  {JSON.stringify(policy.filter_condition, null, 2)}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* ABAC Tab */}
        <TabsContent value="abac" className="space-y-3">
          <div className="flex justify-end">
            <Button onClick={() => setAbacCreateOpen(true)} className="rounded-xl bg-primary text-primary-foreground active:scale-95 text-xs" data-testid="create-abac-btn">
              <Lock className="w-3.5 h-3.5 mr-1.5" /> Add Policy
            </Button>
          </div>
          <div className="stagger-children" data-testid="abac-policies-list">
            {abacPolicies.map(policy => (
              <div key={policy.policy_id} className="glass-card rounded-2xl p-5" data-testid={`abac-${policy.policy_id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-xl ${policy.effect === 'allow' ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-red-500/10 border-red-500/20'} border`}>
                      {policy.effect === 'allow' ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <ShieldAlert className="w-5 h-5 text-red-400" />}
                    </div>
                    <div>
                      <h3 className="text-sm font-heading font-semibold text-foreground">{policy.name}</h3>
                      <p className="text-xs text-muted-foreground">{policy.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={policy.effect === 'allow' ? 'default' : 'destructive'} className="text-[10px]">
                      {policy.effect.toUpperCase()}
                    </Badge>
                    <Badge variant="outline" className="text-[10px] border-border/50">Action: {policy.action}</Badge>
                    <Switch
                      checked={policy.enabled !== false}
                      onCheckedChange={() => toggleAbacPolicy(policy.policy_id)}
                      data-testid={`abac-toggle-${policy.policy_id}`}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  {policy.conditions?.map((cond, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs p-2 rounded-lg bg-muted/30">
                      <Badge variant="outline" className="text-[9px]">{cond.attribute_source}</Badge>
                      <span className="text-muted-foreground font-mono">{cond.attribute}</span>
                      <span className="text-primary font-medium">{cond.operator}</span>
                      <span className="text-foreground font-mono">{JSON.stringify(cond.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Denials Tab — audit of permission-denied events */}
        <TabsContent value="denials" className="space-y-3">
          <div className="glass-card rounded-2xl p-4" data-testid="denials-panel">
            <div className="flex items-center gap-2 mb-1">
              <Ban className="w-4 h-4 text-amber-400" />
              <h3 className="text-sm font-heading font-semibold text-foreground">Permission Denials</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-4">Every time a user's role blocks an action (403), it's logged here. Frequent denials for the same permission may mean a role is over-restricted.</p>

            {roleSuggestions.length > 0 && (
              <div className="mb-4 rounded-xl border border-primary/25 bg-primary/5 p-3" data-testid="role-suggestions">
                <p className="text-[10px] uppercase tracking-wider text-primary/90 mb-2 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Role suggestions</p>
                <div className="space-y-1.5">
                  {roleSuggestions.map((sug, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs flex-wrap" data-testid={`role-suggestion-${i}`}>
                      <span className="text-foreground/90">
                        Role <Badge variant="outline" className="text-[9px] border-border/50 mx-0.5">{sug.role || 'unknown'}</Badge>
                        was denied <span className="font-mono text-amber-300">{sug.permission}</span> ×{sug.count} —
                      </span>
                      {sug.isCustom ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 text-[10px] gap-1"
                          disabled={applyingSuggestion === `${sug.role}:${sug.permission}`}
                          onClick={() => applySuggestion(sug)}
                          data-testid={`apply-suggestion-${i}`}
                        >
                          {applyingSuggestion === `${sug.role}:${sug.permission}` ? 'Adding…' : `Grant to ${sug.role}`}
                        </Button>
                      ) : (
                        <span className="text-[10px] text-muted-foreground italic" data-testid={`suggestion-builtin-note-${i}`}>
                          built-in role — assign a different role or create a custom role with this permission
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {denials.summary.length > 0 && (
              <div className="mb-4">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Most-denied permissions</p>
                <div className="flex flex-wrap gap-2" data-testid="denials-summary">
                  {denials.summary.map((s, i) => (
                    <Badge key={i} variant="outline" className="text-[10px] border-amber-500/30 text-amber-300">
                      {s.permission} · {s.role || 'unknown'} <span className="ml-1 text-amber-400 font-semibold">×{s.count}</span>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {denials.items.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground text-xs" data-testid="denials-empty">
                <CheckCircle2 className="w-6 h-6 mx-auto mb-2 text-emerald-400/70" />
                No permission denials recorded yet.
              </div>
            ) : (
              <div className="overflow-auto" data-testid="denials-list">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b border-border/30">
                      <th className="py-2 pr-3 font-medium">When</th>
                      <th className="py-2 pr-3 font-medium">User</th>
                      <th className="py-2 pr-3 font-medium">Role</th>
                      <th className="py-2 pr-3 font-medium">Needed</th>
                      <th className="py-2 pr-3 font-medium">Request</th>
                    </tr>
                  </thead>
                  <tbody>
                    {denials.items.map((d, i) => (
                      <tr key={d.denial_id || i} className="border-b border-border/15" data-testid={`denial-row-${i}`}>
                        <td className="py-2 pr-3 text-muted-foreground whitespace-nowrap">{d.ts ? new Date(d.ts).toLocaleString() : '—'}</td>
                        <td className="py-2 pr-3 text-foreground/90">{d.user_email || d.user_id || '—'}</td>
                        <td className="py-2 pr-3"><Badge variant="outline" className="text-[9px] border-border/50">{d.role || '—'}</Badge></td>
                        <td className="py-2 pr-3 font-mono text-amber-300">{d.required_permission}</td>
                        <td className="py-2 pr-3 font-mono text-muted-foreground truncate max-w-[220px]">{d.method} {d.path}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>
        {canManageUsers && (
          <TabsContent value="impersonation" className="space-y-3">
            <ImpersonationAuditLog />
          </TabsContent>
        )}
      </Tabs>

      <ConfirmDialogElement />

      {/* Reset Password Dialog */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/20 border border-border/30">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">
                {resetUserName?.[0]?.toUpperCase() || 'U'}
              </div>
              <p className="text-sm text-foreground">{resetUserName}</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">New Password</Label>
              <Input
                data-testid="reset-password-input"
                type="password"
                value={resetPassword}
                onChange={e => setResetPassword(e.target.value)}
                placeholder="Minimum 10 characters"
                className="rounded-lg bg-muted/50 border-border/50"
              />
            </div>
            {resetPassword.length > 0 && (
              <ul className="space-y-1" data-testid="reset-password-requirements">
                {passwordRules(resetPassword).map((r, i) => (
                  <li key={i} className={`flex items-center gap-1.5 text-xs ${r.ok ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                    {r.ok ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" /> : <XCircle className="w-3.5 h-3.5 shrink-0 opacity-50" />}
                    {r.label}
                  </li>
                ))}
              </ul>
            )}
            <Button
              data-testid="confirm-reset-pw"
              onClick={handleResetPassword}
              disabled={resetLoading || !passwordMeetsRules(resetPassword)}
              className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95"
            >
              <KeyRound className="w-4 h-4 mr-2" />
              {resetLoading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin Edit User (name / email — no confirmation link) */}
      <Dialog open={editUserOpen} onOpenChange={setEditUserOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-sm" data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle className="font-heading text-foreground">Edit user</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Name</Label>
              <Input value={editUser.name} onChange={e => setEditUser(p => ({ ...p, name: e.target.value }))} className="rounded-lg bg-muted/50 border-border/50" data-testid="edit-user-name" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Email</Label>
              <Input type="email" value={editUser.email} onChange={e => setEditUser(p => ({ ...p, email: e.target.value }))} className="rounded-lg bg-muted/50 border-border/50" data-testid="edit-user-email" />
              <p className="text-[10px] text-muted-foreground">Changing the email updates the user's sign-in address immediately — no confirmation link is sent.</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Department</Label>
              <DepartmentSelect value={editUser.department} onChange={v => setEditUser(p => ({ ...p, department: v }))} departments={departments} testid="edit-user-department" />
            </div>
            <Button onClick={saveUserEdit} disabled={editSaving || !editUser.email.trim()} className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95" data-testid="edit-user-save">
              {editSaving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Role Editor Dialog */}
      <Dialog open={editRoleOpen} onOpenChange={setEditRoleOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">{editRoleIsNew ? 'Create Role' : 'Edit Role: ' + editRoleName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2 flex-1 overflow-auto">
            {editRoleIsNew && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Role Name</Label>
                <Input data-testid="role-name-input" value={editRoleName} onChange={e => setEditRoleName(e.target.value.toLowerCase().replace(/\s+/g, '_'))} placeholder="e.g. data_engineer" className="rounded-lg bg-muted/50 border-border/50 font-mono" />
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Description</Label>
              <Input value={editRoleDesc} onChange={e => setEditRoleDesc(e.target.value)} placeholder="What this role is for" className="rounded-lg bg-muted/50 border-border/50" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">AI Persona <span className="text-muted-foreground/80">(guides the AI Explore agent for users in this role)</span></Label>
              <Textarea
                data-testid="role-ai-persona-input"
                value={editRolePersona}
                onChange={e => setEditRolePersona(e.target.value)}
                placeholder="e.g. You assist an underwriter — emphasize loss ratios, premium adequacy, and rate change; use P&C insurance terminology."
                rows={3}
                className="rounded-lg bg-muted/50 border-border/50 text-xs"
              />
              <p className="text-[9px] text-muted-foreground/70">Advisory only — shapes tone, focus and terminology. It does not change what data the user can access (row/column security and permissions still apply). Layered on top of the model's own persona.</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-muted-foreground">Permissions ({editRolePerms.length} selected)</Label>
                <div className="flex gap-1">
                  <button onClick={() => { const all = Object.values(allPermissions).flat(); setEditRolePerms([...all]); }} className="text-[9px] text-primary hover:underline">Select All</button>
                  <button onClick={() => setEditRolePerms([])} className="text-[9px] text-muted-foreground hover:underline ml-2">Clear</button>
                </div>
              </div>
              <div className="space-y-3 max-h-60 overflow-auto">
                {Object.entries(allPermissions).map(([category, perms]) => (
                  <div key={category}>
                    <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1">{(PERM_CATEGORIES[category] || {}).label || category.replace(/_/g, ' ')}</p>
                    <div className="flex flex-wrap gap-1">
                      {perms.map(p => (
                        <button key={p} onClick={() => togglePerm(p)} title={p} className={'px-2 py-1 rounded-md text-[9px] border transition-all ' + (editRolePerms.includes(p) ? 'bg-primary/20 text-primary border-primary/30' : 'bg-muted/20 text-muted-foreground border-border/20 hover:border-border/40')}>
                          {permActionLabel(p)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="pt-3 border-t border-border/30">
            <Button onClick={handleSaveRole} disabled={editRoleLoading || !editRoleName.trim()} className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95" data-testid="save-role-btn">
              <Shield className="w-4 h-4 mr-2" />
              {editRoleLoading ? 'Saving...' : editRoleIsNew ? 'Create Role' : 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Create User</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Full Name</Label>
              <Input data-testid="new-user-name" value={newUserName} onChange={e => setNewUserName(e.target.value)} placeholder="Jane Doe" className="rounded-lg bg-muted/50 border-border/50" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Email <span className="text-destructive">*</span></Label>
              <Input data-testid="new-user-email" type="email" value={newUserEmail} onChange={e => setNewUserEmail(e.target.value)} placeholder="jane@company.com" className="rounded-lg bg-muted/50 border-border/50" required />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Password <span className="text-destructive">*</span></Label>
              <Input data-testid="new-user-password" type="password" value={newUserPassword} onChange={e => setNewUserPassword(e.target.value)} placeholder="Minimum 10 characters" className="rounded-lg bg-muted/50 border-border/50" required />
              {newUserPassword.length > 0 && (
                <ul className="space-y-1 pt-1" data-testid="new-user-password-requirements">
                  {passwordRules(newUserPassword).map((r, i) => (
                    <li key={i} className={`flex items-center gap-1.5 text-xs ${r.ok ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                      {r.ok ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" /> : <XCircle className="w-3.5 h-3.5 shrink-0 opacity-50" />}
                      {r.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Role</Label>
              <Select value={newUserRole} onValueChange={setNewUserRole}>
                <SelectTrigger className="rounded-lg bg-muted/50 border-border/50"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                  <SelectItem value="analyst">Analyst</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Department</Label>
              <DepartmentSelect value={newUserDept} onChange={setNewUserDept} departments={departments} testid="new-user-department" />
            </div>
            <Button
              data-testid="confirm-create-user"
              onClick={handleCreateUser}
              disabled={createUserLoading || !newUserEmail || !passwordMeetsRules(newUserPassword)}
              className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95"
            >
              <Users className="w-4 h-4 mr-2" />
              {createUserLoading ? 'Creating...' : 'Create User'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create RLS Policy Dialog */}
      <Dialog open={rlsCreateOpen} onOpenChange={(v) => { setRlsCreateOpen(v); if (!v) setRlsPreview(null); }}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Add Row-Level Security Policy</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Name <span className="text-destructive">*</span></Label>
              <Input data-testid="rls-name" value={rlsDraft.name} onChange={e => setRlsDraft(d => ({ ...d, name: e.target.value }))} placeholder="East Coast States Only" className="rounded-lg bg-muted/50 border-border/50" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Description</Label>
              <Input data-testid="rls-description" value={rlsDraft.description} onChange={e => setRlsDraft(d => ({ ...d, description: e.target.value }))} placeholder="Restrict to eastern US states" className="rounded-lg bg-muted/50 border-border/50" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Model <span className="text-destructive">*</span></Label>
              <Select value={rlsDraft.model_id || undefined} onValueChange={onSelectRlsModel}>
                <SelectTrigger className="rounded-lg bg-muted/50 border-border/50" data-testid="rls-model"><SelectValue placeholder={rlsModelLoading && !rlsModels.length ? 'Loading models…' : 'Select a semantic model'} /></SelectTrigger>
                <SelectContent>
                  {[...rlsModels].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(m => <SelectItem key={m.model_id} value={m.model_id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Table <span className="text-destructive">*</span></Label>
              <Select value={rlsDraft.entity_id || undefined} onValueChange={onSelectRlsEntity} disabled={!rlsDraft.model_id || rlsModelLoading}>
                <SelectTrigger className="rounded-lg bg-muted/50 border-border/50" data-testid="rls-table"><SelectValue placeholder={!rlsDraft.model_id ? 'Pick a model first' : (rlsModelLoading ? 'Loading tables…' : 'Select a table')} /></SelectTrigger>
                <SelectContent>
                  {[...rlsEntities].sort((a, b) => (a.name || a.table_name || '').localeCompare(b.name || b.table_name || '')).map(e => <SelectItem key={e.entity_id} value={e.entity_id}>{e.name || e.table_name}{e.entity_type ? ` · ${e.entity_type}` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Apply to role (blank = whole tenant)</Label>
              <Select value={rlsDraft.role || 'none'} onValueChange={v => setRlsDraft(d => ({ ...d, role: v === 'none' ? '' : v }))}>
                <SelectTrigger className="rounded-lg bg-muted/50 border-border/50" data-testid="rls-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">All users (tenant)</SelectItem>
                  {roles.map(r => <SelectItem key={r.role_name} value={r.role_name}>{r.role_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2" data-testid="rls-conditions">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Conditions <span className="text-destructive">*</span></Label>
                {rlsDraft.conditions.length > 1 && <span className="text-[10px] text-muted-foreground">all must match (AND)</span>}
              </div>
              {rlsDraft.conditions.map((c, ci) => {
                const usedElsewhere = rlsDraft.conditions.filter(x => x.key !== c.key).map(x => x.field);
                const availAttrs = rlsEntityAttrs
                  .filter(a => a.name === c.field || !usedElsewhere.includes(a.name))
                  .sort((a, b) => (a.business_name || a.name || '').localeCompare(b.business_name || b.name || ''));
                return (
                  <div key={c.key} className="rounded-lg border border-border/50 bg-muted/20 p-2.5 space-y-2" data-testid={`rls-condition-${ci}`}>
                    {ci > 0 && <div className="text-[10px] font-semibold text-primary/70 -mt-0.5">AND</div>}
                    <div className="flex items-start gap-2">
                      <Select value={c.field || undefined} onValueChange={(v) => onSelectConditionField(c.key, v)} disabled={!rlsDraft.entity_id}>
                        <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 flex-1" data-testid={`rls-cond-field-${ci}`}><SelectValue placeholder={!rlsDraft.entity_id ? 'Pick a table first' : 'Select a field'} /></SelectTrigger>
                        <SelectContent>
                          {availAttrs.map(a => (
                            <SelectItem key={a.name} value={a.name}>
                              {a.business_name || a.name}
                              <span className="text-muted-foreground ml-1 text-[10px]">({(a.data_type || 'text')})</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {rlsDraft.conditions.length > 1 && (
                        <Button type="button" size="icon" variant="ghost" className="h-9 w-9 flex-shrink-0 text-muted-foreground hover:text-destructive" onClick={() => removeCondition(c.key)} data-testid={`rls-cond-remove-${ci}`}>
                          <XIcon className="w-4 h-4" />
                        </Button>
                      )}
                    </div>

                    {c.field && c.field_type === 'date' && (
                      <div className="flex items-center gap-2 flex-wrap" data-testid={`rls-cond-date-${ci}`}>
                        <Select value={c.operator || 'on_or_after'} onValueChange={(v) => updateCondition(c.key, { operator: v, value: '', value2: '' })}>
                          <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 w-[210px]" data-testid={`rls-cond-dateop-${ci}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {DATE_OPERATORS.map(o => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {c.operator === 'rolling' ? (
                          <div className="flex items-center gap-1.5 flex-1">
                            <Input type="number" min="1" value={c.value} onChange={e => updateCondition(c.key, { value: e.target.value })} placeholder="e.g. 90" className="rounded-lg bg-muted/50 border-border/50 w-28" data-testid={`rls-cond-date-days-${ci}`} />
                            <span className="text-[11px] text-muted-foreground">days (resolved at query time)</span>
                          </div>
                        ) : (
                          <>
                            <Input type="date" value={c.value} onChange={e => updateCondition(c.key, { value: e.target.value })} className="rounded-lg bg-muted/50 border-border/50 flex-1" data-testid={`rls-cond-date-value-${ci}`} />
                            {c.operator === 'between' && (
                              <>
                                <span className="text-[10px] text-muted-foreground">and</span>
                                <Input type="date" value={c.value2} onChange={e => updateCondition(c.key, { value2: e.target.value })} className="rounded-lg bg-muted/50 border-border/50 flex-1" data-testid={`rls-cond-date-value2-${ci}`} />
                              </>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {c.field && c.field_type === 'numeric' && (
                      <div className="flex items-center gap-2" data-testid={`rls-cond-numeric-${ci}`}>
                        <Select value={c.operator || 'eq'} onValueChange={(v) => updateCondition(c.key, { operator: v })}>
                          <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 w-[170px]" data-testid={`rls-cond-op-${ci}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {NUMERIC_OPERATORS.map(o => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input type="number" value={c.value} onChange={e => updateCondition(c.key, { value: e.target.value })} placeholder="e.g. 50" className="rounded-lg bg-muted/50 border-border/50 flex-1" data-testid={`rls-cond-value-${ci}`} />
                        {c.operator === 'between' && (
                          <>
                            <span className="text-[10px] text-muted-foreground">and</span>
                            <Input type="number" value={c.value2} onChange={e => updateCondition(c.key, { value2: e.target.value })} placeholder="e.g. 100" className="rounded-lg bg-muted/50 border-border/50 flex-1" data-testid={`rls-cond-value2-${ci}`} />
                          </>
                        )}
                      </div>
                    )}

                    {c.field && c.field_type === 'text' && (
                      <div data-testid={`rls-cond-text-${ci}`}>
                        <Popover open={c.pickerOpen} onOpenChange={(o) => updateCondition(c.key, { pickerOpen: o })}>
                          <PopoverTrigger asChild>
                            <button type="button" data-testid={`rls-cond-values-trigger-${ci}`}
                              className="w-full flex items-center justify-between gap-2 rounded-lg bg-muted/50 border border-border/50 px-3 py-2 text-left text-sm min-h-[38px]">
                              <span className="flex flex-wrap gap-1 flex-1 min-w-0">
                                {c.values.length === 0
                                  ? <span className="text-muted-foreground">{c.optionsLoading ? 'Loading values…' : 'is any of…'}</span>
                                  : c.values.map(v => (
                                      <Badge key={String(v)} variant="outline" className="text-[10px] gap-1 border-primary/40">
                                        {String(v)}
                                        <XIcon className="w-2.5 h-2.5 cursor-pointer" onClick={(ev) => { ev.stopPropagation(); toggleConditionValue(c.key, v); }} />
                                      </Badge>
                                    ))}
                              </span>
                              <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 flex-shrink-0" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                            <Command shouldFilter={true}>
                              <CommandInput placeholder="Search values…" className="text-xs" data-testid={`rls-cond-values-search-${ci}`} />
                              <CommandList>
                                {c.optionsLoading
                                  ? <div className="py-4 flex items-center justify-center text-xs text-muted-foreground"><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />Loading…</div>
                                  : <>
                                      <CommandEmpty>No matching values.</CommandEmpty>
                                      <CommandGroup>
                                        {c.options.map(opt => {
                                          const sel = c.values.some(v => String(v) === String(opt));
                                          return (
                                            <CommandItem key={String(opt)} value={String(opt)} onSelect={() => toggleConditionValue(c.key, opt)} data-testid={`rls-cond-opt-${ci}-${String(opt)}`}>
                                              <Checkbox checked={sel} className="mr-2 pointer-events-none" />
                                              <span className="truncate">{String(opt)}</span>
                                            </CommandItem>
                                          );
                                        })}
                                      </CommandGroup>
                                    </>}
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                        {c.truncated && <p className="text-[10px] text-muted-foreground mt-1">Showing first 1000 — type to search for more.</p>}
                      </div>
                    )}
                  </div>
                );
              })}
              <Button type="button" variant="outline" size="sm" className="w-full rounded-lg text-xs border-dashed" onClick={addCondition} disabled={!rlsDraft.entity_id || rlsEntityAttrs.length <= rlsDraft.conditions.length} data-testid="rls-add-condition">
                + Add condition
              </Button>
            </div>
            <div className="rounded-lg border border-border/40 p-2.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Preview — who can see what</span>
                <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1" onClick={previewRls} disabled={rlsPreviewLoading} data-testid="rls-preview-btn">
                  {rlsPreviewLoading ? 'Checking…' : 'Preview'}
                </Button>
              </div>
              {rlsPreview && (
                <div className="mt-2 text-xs" data-testid="rls-preview-result">
                  <p className="text-foreground">
                    <span className="font-semibold text-emerald-400">{rlsPreview.visible_rows}</span> of {rlsPreview.total_rows} rows visible
                    <span className="text-muted-foreground"> ({rlsPreview.visible_pct}%)</span>
                    {rlsDraft.role ? <span className="text-muted-foreground"> for role <Badge variant="outline" className="text-[9px] mx-0.5">{rlsDraft.role}</Badge></span> : <span className="text-muted-foreground"> for all users</span>}
                  </p>
                  {rlsPreview.note && <p className="text-[10px] text-amber-300/80 mt-1">{rlsPreview.note}</p>}
                  {rlsPreview.sample?.length > 0 && (
                    <pre className="mt-2 font-mono text-[9px] text-muted-foreground bg-muted/40 p-2 rounded overflow-auto max-h-28">{JSON.stringify(rlsPreview.sample, null, 1)}</pre>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={rlsDraft.enabled} onCheckedChange={v => setRlsDraft(d => ({ ...d, enabled: v }))} data-testid="rls-enabled" />
              <Label className="text-xs text-muted-foreground">Enable immediately</Label>
            </div>
            <Button data-testid="confirm-create-rls" onClick={handleCreateRls} disabled={rlsSaving} className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95">
              <Filter className="w-4 h-4 mr-2" />{rlsSaving ? 'Creating…' : 'Create Policy'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create ABAC Policy Dialog */}
      <Dialog open={abacCreateOpen} onOpenChange={setAbacCreateOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Add ABAC Policy</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Name <span className="text-destructive">*</span></Label>
              <Input data-testid="abac-name" value={abacDraft.name} onChange={e => setAbacDraft(d => ({ ...d, name: e.target.value }))} placeholder="Admin Delete Only" className="rounded-lg bg-muted/50 border-border/50" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Description</Label>
              <Input data-testid="abac-description" value={abacDraft.description} onChange={e => setAbacDraft(d => ({ ...d, description: e.target.value }))} placeholder="Only admins can delete resources" className="rounded-lg bg-muted/50 border-border/50" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Action <span className="text-destructive">*</span></Label>
                <Input data-testid="abac-action" value={abacDraft.action} onChange={e => setAbacDraft(d => ({ ...d, action: e.target.value }))} placeholder="write / delete / read" className="rounded-lg bg-muted/50 border-border/50" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Effect</Label>
                <Select value={abacDraft.effect} onValueChange={v => setAbacDraft(d => ({ ...d, effect: v }))}>
                  <SelectTrigger className="rounded-lg bg-muted/50 border-border/50" data-testid="abac-effect"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="allow">Allow</SelectItem>
                    <SelectItem value="deny">Deny</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="rounded-lg border border-border/40 p-2.5 space-y-2">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Condition (on the acting user)</p>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">Attribute</Label>
                  <Input data-testid="abac-attribute" value={abacDraft.attribute} onChange={e => setAbacDraft(d => ({ ...d, attribute: e.target.value }))} placeholder="role" className="rounded-lg bg-muted/50 border-border/50 h-8 text-xs" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">Operator</Label>
                  <Select value={abacDraft.operator} onValueChange={v => setAbacDraft(d => ({ ...d, operator: v }))}>
                    <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 h-8 text-xs" data-testid="abac-operator"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="equals">equals</SelectItem>
                      <SelectItem value="not_equals">not equals</SelectItem>
                      <SelectItem value="in">in</SelectItem>
                      <SelectItem value="not_in">not in</SelectItem>
                      <SelectItem value="contains">contains</SelectItem>
                      <SelectItem value="gte">&ge;</SelectItem>
                      <SelectItem value="lte">&le;</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">Value</Label>
                  <Input data-testid="abac-value" value={abacDraft.value} onChange={e => setAbacDraft(d => ({ ...d, value: e.target.value }))} placeholder="admin" className="rounded-lg bg-muted/50 border-border/50 h-8 text-xs" />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={abacDraft.enabled} onCheckedChange={v => setAbacDraft(d => ({ ...d, enabled: v }))} data-testid="abac-enabled" />
              <Label className="text-xs text-muted-foreground">Enable immediately</Label>
            </div>
            <Button data-testid="confirm-create-abac" onClick={handleCreateAbac} disabled={abacSaving} className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95">
              <Lock className="w-4 h-4 mr-2" />{abacSaving ? 'Creating…' : 'Create Policy'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
